import React, { useState, useCallback, useEffect, useRef } from 'react';
import { useDropzone } from 'react-dropzone';
import { useNavigate, useSearchParams, Link } from 'react-router-dom';
import { useUpload } from '../hooks/useUpload';
import api from '../services/api';
import StorageBar from '../components/StorageBar';
import './UploadPage.css';

const ACCEPTED = {
  'image/jpeg': [], 'image/png': [], 'image/webp': [], 'image/gif': [],
  'video/mp4': [], 'video/webm': [], 'video/quicktime': [],
};

// Max number of files uploading at the same time
const CONCURRENCY = 3;

function formatBytes(bytes) {
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
}

/**
 * Single file upload row.
 * Accepts an `autoStart` prop — when true the upload begins immediately
 * on mount, enabling the queue-based simultaneous upload flow.
 */
function UploadJob({ file, eventId, autoStart, onDone, onStarted, onFinished }) {
  const { upload, cancel, reset, progress, status, error, isQuotaError } = useUpload();
  const startedRef = useRef(false);

  useEffect(() => {
    if (autoStart && !startedRef.current && eventId) {
      startedRef.current = true;
      upload(file, { eventId }, onDone, onStarted, onFinished);
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [autoStart, eventId, file, upload, onDone]);

  const isDone    = status === 'done';
  const isError   = status === 'error';
  const isIdle    = status === 'idle';

  return (
    <div className={`upload-job upload-job--${status}`}>
      <div className="upload-job__info">
        <span className="upload-job__name" title={file.name}>{file.name}</span>
        <span className="upload-job__size">{formatBytes(file.size)}</span>
      </div>

      {/* Progress bar — shown while uploading */}
      {status === 'uploading' && (
        <div className="upload-job__progress">
          <div className="progress-bar">
            <div className="progress-fill" style={{ width: `${progress}%` }} />
          </div>
          <span className="progress-label">{progress}%</span>
          <button className="btn btn-ghost btn-xs" onClick={cancel}>✕</button>
        </div>
      )}

      {/* Queued — waiting its turn */}
      {isIdle && autoStart && (
        <p className="upload-job__status upload-job__status--queued">⏳ Queued…</p>
      )}

      {status === 'processing' && (
        <p className="upload-job__status">⚙ Processing…</p>
      )}

      {isDone && (
        <p className="upload-job__status upload-job__status--done">✓ Done</p>
      )}

      {isError && (
        <div className={`upload-job__error ${isQuotaError ? 'upload-job__error--quota' : ''}`}>
          <span title={error}>{isQuotaError ? '🚫' : '✕'} {error}</span>
          {!isQuotaError && (
            <button
              className="btn btn-ghost btn-xs"
              onClick={() => { reset(); startedRef.current = false; }}
            >
              Retry
            </button>
          )}
        </div>
      )}
    </div>
  );
}

export default function UploadPage() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const preSelectedEventId = searchParams.get('eventId');

  const [events, setEvents] = useState([]);
  const [eventsLoading, setEventsLoading] = useState(true);
  const [selectedEventId, setSelectedEventId] = useState(preSelectedEventId || '');

  // fileList: array of { id, file, done }
  const [fileList, setFileList] = useState([]);
  // How many uploads have finished (done OR error counts as finished for queue purposes)
  const doneCount = fileList.filter((f) => f.done).length;

  // activeCount: files currently uploading (not yet done)
  // We auto-start the first CONCURRENCY files; as each finishes, the next one starts.
  // We implement this by tracking a `startedCount` — files whose index < startedCount get autoStart=true.
  const [startedCount, setStartedCount] = useState(0);
  const [storageData, setStorageData] = useState(null);

  useEffect(() => {
    api.get('/api/events')
      .then(({ data }) => {
        setEvents(data.events);
        if (!preSelectedEventId && data.events.length === 1) {
          setSelectedEventId(data.events[0].id);
        }
      })
      .catch(() => {})
      .finally(() => setEventsLoading(false));
  }, [preSelectedEventId]);

  // When new files are added or a slot opens up, kick off up to CONCURRENCY uploads
  useEffect(() => {
    if (!selectedEventId) return;
    const toStart = Math.min(fileList.length, startedCount + CONCURRENCY) ;
    if (toStart > startedCount) setStartedCount(toStart);
  }, [fileList.length, selectedEventId, startedCount]);

  const handleFileDone = useCallback((index) => {
    setFileList((prev) => {
      const next = prev.map((f, i) => i === index ? { ...f, done: true } : f);
      return next;
    });
    // Open a new slot
    setStartedCount((n) => Math.min(fileList.length, n + 1));
  }, [fileList.length]);

  const onDrop = useCallback((accepted) => {
    if (!accepted.length) return;
    setFileList((prev) => {
      const newEntries = accepted.map((file) => ({ file, done: false, id: `${file.name}-${file.size}-${Date.now()}-${Math.random()}` }));
      const next = [...prev, ...newEntries];
      // Immediately grant slots to the newly added files if slots are free
      setStartedCount((n) => Math.min(next.length, Math.max(n, CONCURRENCY)));
      return next;
    });
  }, []);

  const { getRootProps, getInputProps, isDragActive } = useDropzone({
    onDrop,
    accept: ACCEPTED,
    maxSize: 500 * 1024 * 1024,
    multiple: true,
    disabled: !selectedEventId || storageData?.usedPercent >= 100,
  });

  const selectedEvent = events.find((e) => e.id === selectedEventId);
  const allDone = fileList.length > 0 && doneCount === fileList.length;
  const activeCount = Math.min(startedCount, fileList.length) - doneCount;
  const queuedCount = fileList.length - Math.min(startedCount, fileList.length);

  // Track mediaIds of in-progress uploads so we can clean them up on unload
  const activeMediaIdsRef = React.useRef(new Set());

  function registerMediaId(mediaId) {
    activeMediaIdsRef.current.add(mediaId);
  }
  function deregisterMediaId(mediaId) {
    activeMediaIdsRef.current.delete(mediaId);
  }

  const hasActiveUploads = fileList.some((f) => !f.done);

  // Block page refresh / tab close while uploads are in progress
  useEffect(() => {
    function handleBeforeUnload(e) {
      if (!hasActiveUploads) return;
      e.preventDefault();
      e.returnValue = ''; // required for Chrome to show the dialog

      // Best-effort cleanup via sendBeacon (fires even as page unloads)
      const mediaIds = [...activeMediaIdsRef.current];
      if (mediaIds.length > 0) {
        const token = localStorage.getItem('mv_token');
        const baseUrl = process.env.REACT_APP_API_URL || '';
        const blob = new Blob(
          [JSON.stringify({ mediaIds })],
          { type: 'application/json' }
        );
        // sendBeacon is the only reliable way to fire a request on page unload
        navigator.sendBeacon(
          `${baseUrl}/api/upload/cleanup?token=${encodeURIComponent(token)}`,
          blob
        );
      }
    }

    window.addEventListener('beforeunload', handleBeforeUnload);
    return () => window.removeEventListener('beforeunload', handleBeforeUnload);
  }, [hasActiveUploads]);

  function handleClearDone() {
    setFileList((prev) => {
      const remaining = prev.filter((f) => !f.done);
      setStartedCount(Math.min(remaining.length, CONCURRENCY));
      return remaining;
    });
  }

  return (
    <div className="upload-page page-wrapper">
      <div className="upload-page__header">
        <h1>Upload media</h1>
        <button
          className="btn btn-ghost"
          onClick={() => navigate(selectedEventId ? `/events/${selectedEventId}` : '/events')}
        >
          ← Back
        </button>
      </div>

      {/* Event selector */}
      <div className="upload-event-selector">
        <label className="upload-event-label">Upload to event *</label>
        {eventsLoading ? (
          <div className="spinner" />
        ) : events.length === 0 ? (
          <div className="upload-no-events">
            <p>You need to create an event first.</p>
            <Link to="/events" className="btn btn-primary btn-sm">Go to events</Link>
          </div>
        ) : (
          <select
            className="upload-event-select"
            value={selectedEventId}
            onChange={(e) => setSelectedEventId(e.target.value)}
          >
            <option value="">— Select an event —</option>
            {events.map((ev) => (
              <option key={ev.id} value={ev.id}>{ev.name}</option>
            ))}
          </select>
        )}
      </div>

      {/* Storage indicator */}
      <div className="upload-storage">
        <StorageBar compact onStorageLoaded={setStorageData} />
      </div>

      {/* Quota full banner */}
      {storageData?.usedPercent >= 100 && (
        <div className="banner banner-error upload-quota-banner">
          🚫 Storage limit reached. Delete some files to free up space before uploading.
        </div>
      )}

      {/* Dropzone */}
      <div
        {...getRootProps()}
        className={`dropzone ${isDragActive ? 'dropzone--active' : ''} ${!selectedEventId ? 'dropzone--disabled' : ''}`}
      >
        <input {...getInputProps()} />
        <div className="dropzone__icon">☁</div>
        <p className="dropzone__primary">
          {!selectedEventId
            ? 'Select an event above to enable upload'
            : isDragActive
            ? 'Drop files here'
            : `Drop files here or click to browse`}
        </p>
        {selectedEventId && (
          <p className="dropzone__secondary">
            Upload to <strong>{selectedEvent?.name}</strong> — JPG, PNG, WebP, GIF, MP4, WebM, MOV up to 500 MB
          </p>
        )}
        {selectedEventId && (
          <p className="dropzone__secondary" style={{ marginTop: '0.25rem', opacity: 0.6 }}>
            Up to {CONCURRENCY} files upload simultaneously
          </p>
        )}
      </div>

      {/* Queue stats + job list */}
      {fileList.length > 0 && (
        <div className="upload-jobs">
          <div className="upload-jobs__header">
            <div className="upload-queue-stats">
              <span>{fileList.length} file{fileList.length !== 1 ? 's' : ''}</span>
              {activeCount > 0 && (
                <span className="queue-stat queue-stat--active">↑ {activeCount} uploading</span>
              )}
              {queuedCount > 0 && (
                <span className="queue-stat queue-stat--queued">⏳ {queuedCount} queued</span>
              )}
              {doneCount > 0 && (
                <span className="queue-stat queue-stat--done">✓ {doneCount} done</span>
              )}
            </div>
            <div className="upload-jobs__actions">
              {doneCount > 0 && !allDone && (
                <button className="btn btn-ghost btn-xs" onClick={handleClearDone}>
                  Clear done
                </button>
              )}
              {allDone && (
                <button
                  className="btn btn-primary btn-sm"
                  onClick={() => navigate(`/events/${selectedEventId}`)}
                >
                  View event →
                </button>
              )}
            </div>
          </div>

          {fileList.map((entry, index) => (
            <UploadJob
              key={entry.id}
              file={entry.file}
              eventId={selectedEventId}
              autoStart={index < startedCount}
              onDone={() => handleFileDone(index)}
              onStarted={registerMediaId}
              onFinished={deregisterMediaId}
            />
          ))}
        </div>
      )}
    </div>
  );
}
